import {Box, TextField, Select, MenuItem, FormControl, Typography} from "@mui/material";

const COUNTRY_CODES = [
  {code: "+972", label: "IL +972", flag: "🇮🇱", minLength: 9, maxLength: 9},
  {code: "+1", label: "US +1", flag: "🇺🇸", minLength: 10, maxLength: 10},
];

// Validate phone number length based on country code
const validatePhoneNumber = (countryCode, localNumber) => {
  if (!localNumber) return {isValid: true, message: ""};

  const country = COUNTRY_CODES.find(c => c.code === countryCode);
  if (!country) return {isValid: true, message: ""};

  if (localNumber.length < country.minLength) {
    return {isValid: false, message: `מספר קצר מדי (נדרש ${country.minLength} ספרות)`};
  }
  if (localNumber.length > country.maxLength) {
    return {isValid: false, message: `מספר ארוך מדי (מקסימום ${country.maxLength} ספרות)`};
  }
  return {isValid: true, message: ""};
};

// Check if the original value was corrupted and needs fixing
// Only show corruption if the stored format is wrong AND the number is complete
const isCorruptedNumber = (original, parsed, validation) => {
  if (!original) return false;
  // Don't show corruption warning if the number is incomplete (still being typed)
  if (!validation.isValid) return false;

  const cleanedOriginal = original.replace(/[^\d+]/g, '');
  const expectedClean = parsed.countryCode + parsed.localNumber;

  // If the cleaned original matches expected, it's not corrupted
  if (cleanedOriginal === expectedClean || original === expectedClean) {
    return false;
  }

  return true;
};

const PhoneInput = ({value, onChange, disabled = false, error, helperText, sx = {}}) => {

  // Parse the phone number to extract country code and local number
  // Handles corrupted formats like: 0655555555, +972972972544616141, +1 (561) 221-7227, 58-500-3429⁩0
  const parsePhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return {countryCode: "+972", localNumber: ""};

    // First, extract only digits and + sign (removes all special chars, unicode, etc.)
    let cleaned = '';
    let hasPlus = false;
    for (const char of phoneNumber) {
      if (char === '+' && cleaned.length === 0) {
        hasPlus = true;
      } else if (char >= '0' && char <= '9') {
        cleaned += char;
      }
    }
    if (hasPlus) {
      cleaned = '+' + cleaned;
    }

    // Handle duplicated country codes like +972972972544616141
    while (cleaned.startsWith('+972972')) {
      cleaned = '+972' + cleaned.slice(7);
    }

    // Check for country codes
    for (const country of COUNTRY_CODES) {
      if (cleaned.startsWith(country.code)) {
        let localNum = cleaned.slice(country.code.length);
        // Remove leading 0 if present after country code
        if (localNum.startsWith('0')) {
          localNum = localNum.slice(1);
        }
        return {
          countryCode: country.code,
          localNumber: localNum
        };
      }
    }

    // If starts with 0, assume Israeli number
    if (cleaned.startsWith('0')) {
      return {
        countryCode: "+972",
        localNumber: cleaned.slice(1)
      };
    }

    // Default to +972 with just digits
    return {countryCode: "+972", localNumber: cleaned.replace(/\D/g, '')};
  };

  const parsed = parsePhoneNumber(value);
  const {countryCode, localNumber} = parsed;

  // Check validation
  const validation = validatePhoneNumber(countryCode, localNumber);
  const corrupted = isCorruptedNumber(value, parsed, validation);

  const handleCountryCodeChange = (e) => {
    const newCountryCode = e.target.value;
    const fullNumber = localNumber ? `${newCountryCode}${localNumber}` : "";
    onChange(fullNumber);
  };

  const handleLocalNumberChange = (e) => {
    let input = e.target.value;

    // Remove non-numeric characters
    input = input.replace(/\D/g, '');

    // If user enters number starting with 0, remove it (we'll add country code)
    if (input.startsWith('0')) {
      input = input.slice(1);
    }

    const fullNumber = input ? `${countryCode}${input}` : "";
    onChange(fullNumber);
  };

  // Determine error state
  const showError = error || !validation.isValid || corrupted;
  const errorMessage = helperText || validation.message || (corrupted ? "מספר לא תקין - נא לתקן" : "");

  return (
      <Box sx={{...sx}} dir="ltr">
        <Box sx={{display: "flex", gap: 1}} style={{direction: "ltr"}}>
          <FormControl sx={{minWidth: 110}}>
            <Select
                value={countryCode}
                onChange={handleCountryCodeChange}
                disabled={disabled}
                size="small"
                error={showError}
                sx={{
                  height: "56px",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5
                  }
                }}
            >
              {COUNTRY_CODES.map((country) => (
                  <MenuItem key={country.code} value={country.code}>
                    <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                      <span>{country.flag}</span>
                      <span>{country.code}</span>
                    </Box>
                  </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
              fullWidth
              value={localNumber}
              onChange={handleLocalNumberChange}
              disabled={disabled}
              error={showError}
              placeholder="501234567"
              inputProps={{
                style: {direction: "ltr"},
                inputMode: "tel"
              }}
          />
        </Box>
        {errorMessage && (
            <Typography sx={{color: "#d32f2f", fontSize: "12px", mt: 0.5, mr: 1.5}}>
              {errorMessage}
            </Typography>
        )}
      </Box>
  );
};

// Format phone number for display (e.g., +972-52-631-0634)
// Returns error message if phone number is invalid
export const formatPhoneForDisplay = (phoneNumber) => {
  if (!phoneNumber) return "";

  // Extract only digits and + sign
  let cleaned = '';
  let hasPlus = false;
  for (const char of phoneNumber) {
    if (char === '+' && cleaned.length === 0) hasPlus = true;
    else if (char >= '0' && char <= '9') cleaned += char;
  }
  if (hasPlus) cleaned = '+' + cleaned;

  // Handle duplicated country codes
  while (cleaned.startsWith('+972972')) {
    cleaned = '+972' + cleaned.slice(7);
  }

  // Format based on country code
  if (cleaned.startsWith('+972')) {
    const local = cleaned.slice(4);
    if (local.length === 9) {
      // Israeli mobile: +972-52-631-0634
      return `+972-${local.slice(0, 2)}-${local.slice(2, 5)}-${local.slice(5)}`;
    }
    // Invalid length for Israeli number
    return "מספר טלפון לא תקין נא לעדכן";
  }

  if (cleaned.startsWith('+1')) {
    const local = cleaned.slice(2);
    if (local.length === 10) {
      // US format: +1-561-221-7227
      return `+1-${local.slice(0, 3)}-${local.slice(3, 6)}-${local.slice(6)}`;
    }
    // Invalid length for US number
    return "מספר טלפון לא תקין נא לעדכן";
  }

  // For numbers starting with 0, assume Israeli
  if (cleaned.startsWith('0') && cleaned.length === 10) {
    const local = cleaned.slice(1);
    return `+972-${local.slice(0, 2)}-${local.slice(2, 5)}-${local.slice(5)}`;
  }

  // Unrecognized format - show error message
  return "מספר טלפון לא תקין נא לעדכן";
};

// Export validation function for use in forms
export const isValidPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return false;

  // Extract only digits
  let digits = '';
  let hasPlus = false;
  for (const char of phoneNumber) {
    if (char === '+' && digits.length === 0) hasPlus = true;
    else if (char >= '0' && char <= '9') digits += char;
  }
  if (hasPlus) digits = '+' + digits;

  // Check for valid country code and length
  for (const country of COUNTRY_CODES) {
    if (digits.startsWith(country.code)) {
      const localNum = digits.slice(country.code.length);
      return localNum.length >= country.minLength && localNum.length <= country.maxLength;
    }
  }
  return false;
};

export default PhoneInput;
